"use client";

import { Button } from "@/components/atoms/buttons/";
import { toast } from "@/components/atoms/toast";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useCart } from "@/hooks/useCart";
import { useCustomer } from "@/hooks/useCustomer";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { sdk } from "@/lib/medusa/client";
import { getHomePageSlug } from "@/lib/utils";
import { createAuthSchemas, SigninSchema } from "@/lib/validation/auth";
import { useCompanyDetails } from "@/queries/company";
import { useCheckoutStore } from "@/stores/checkout-store";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState, useTransition } from "react";
import { useForm } from "react-hook-form";

type LoginFormProps = {
  locale: Locales;
};

const LoginForm = ({ locale }: LoginFormProps) => {
  const [isPassVisible, setIsPassVisible] = useState(false);
  const [isPending, startTransition] = useTransition();
  const router = useRouter();
  const { setCustomer } = useCustomer();
  const { cart, setCart } = useCart();
  const queryClient = useQueryClient();
  const { signinSchema } = createAuthSchemas(locale);

  const searchParams = useSearchParams();

  const redirect = searchParams?.get("redirect");
  const companyToken = searchParams.get("token");
  const email = searchParams.get("email");
  const companyName = searchParams.get("company_name");

  const { data: companyDetails } = useCompanyDetails(companyName!);

  const form = useForm<SigninSchema>({
    resolver: zodResolver(signinSchema),
    defaultValues: {
      email: email ?? "",
      password: "",
    },
  });

  const { handleSubmit, control } = form;

  useEffect(() => {
    if (companyToken && email && companyName) {
      const invitationData = {
        token: companyToken,
        email,
        company_name: companyName,
        timestamp: Date.now(),
      };

      if (typeof window !== "undefined") {
        sessionStorage.setItem("invitation_data", JSON.stringify(invitationData));
      }
    }
  }, [companyToken, email, companyName]);

  const onSubmit = async (data: SigninSchema) => {
    if (!cart || !cart.id) return;

    startTransition(async () => {
      let token: string | { location: string };
      try {
        token = await sdk.auth.login("customer", "emailpass", {
          email: data.email,
          password: data.password,
        });
      } catch (error) {
        console.error(error);
        toast.error({ title: __("Invalid email or password.", locale) });
        return;
      }

      if (typeof token !== "string") {
        toast.error({
          title: __("Authentication requires more actions", locale),
          description: __("This isn't supported by this flow.", locale),
        });
        return;
      }

      try {
        const { customer } = await sdk.store.customer.retrieve();
        useCheckoutStore.getState().resetFormData();
        queryClient.removeQueries({ queryKey: ["company-details"] });
        queryClient.removeQueries({ queryKey: ["company-employees"] });
        queryClient.removeQueries({ queryKey: ["company-addresses"] });
        setCustomer(customer);

        if (companyToken && companyDetails && companyDetails.id && customer && customer.id) {
          try {
            await sdk.client.fetch(`store/companies/${companyDetails.id}/invite/process`, {
              method: "POST",
              body: {
                token: companyToken,
                accepted: true,
                customer_id: customer.id,
              },
            });
            if (typeof window !== "undefined") {
              sessionStorage.removeItem("invitation_data");
            }
          } catch (error) {
            console.error("Error processing invitation:", error);
            toast.warning({
              title: __("Account created successfully, but failed to process company invitation.", locale),
            });
          }
        }
      } catch (error) {
        console.warn(error);
        toast.error({
          title: __("Login failed", locale),
          description: __("If you signed up with Google, please use the Google login button.", locale),
        });

        return;
      }

      const { cart: newCart } = await sdk.store.cart.transferCart(cart.id);
      setCart(newCart);

      if (redirect) {
        return router.push(redirect);
      }
      toast.success({ title: __("Login successful !", locale) });
      router.push(getHomePageSlug(locale));
    });
  };

  return (
    <>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="font-geist space-y-6">
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem className="w-full gap-1">
                <div className="flex flex-col gap-2.5">
                  <FormLabel className="mb-0 text-base font-medium">
                    {__("Email Address", locale)} <span className="text-pink-primary">*</span>
                  </FormLabel>

                  <FormControl>
                    <Input
                      disabled={isPending}
                      type="email"
                      className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] bg-white placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                      {...field}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="password"
            render={({ field }) => (
              <FormItem className="w-full gap-1">
                <div className="flex flex-col gap-2.5">
                  <FormLabel className="mb-0 text-base font-medium">
                    {__("Password", locale)} <span className="text-pink-primary">*</span>
                  </FormLabel>

                  <FormControl>
                    <div className="relative">
                      <Input
                        disabled={isPending}
                        className="border-border-light aria-invalid:border-pink-primary font-geist aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                        type={isPassVisible ? "text" : "password"}
                        {...field}
                      />
                      <button
                        onClick={() => setIsPassVisible((state) => !state)}
                        type="button"
                        className="absolute inset-y-0 end-0 cursor-pointer pe-3 pl-3"
                      >
                        {isPassVisible ? (
                          <EyeOff size={20} className="text-dark-primary" />
                        ) : (
                          <Eye size={20} className="text-dark-primary" />
                        )}
                      </button>
                    </div>
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button disabled={isPending} className="h-11 w-full text-base font-semibold" type="submit">
            {isPending ? (
              <>
                <Loader2 className="mr-2 animate-spin" />
              </>
            ) : (
              __("Login", locale)
            )}
          </Button>
        </form>
      </Form>
      <div className="font-geist-sans mt-6 flex items-center justify-between text-sm sm:justify-center sm:gap-3.5">
        <p>
          {__("Not registered yet ?", locale)}{" "}
          <Link
            href={getPermalink(locale, "signup")}
            className="text-pink-primary focus-visible:ring-pink-primary/50 underline outline-none focus-visible:ring-[2px] focus-visible:ring-offset-2"
          >
            {__("Register", locale)}
          </Link>
        </p>
        <hr className="hidden h-[1px] w-2.5 sm:block" />
        <Link
          href={getPermalink(locale, "forgot-password")}
          className="text-pink-primary focus-visible:ring-pink-primary/50 underline outline-none focus-visible:ring-[2px] focus-visible:ring-offset-2"
        >
          {__("Forgot Password ?", locale)}
        </Link>
      </div>
    </>
  );
};

export default LoginForm;
